"use client"
import { Box, Container, Typography, List, ListItem, ListItemText, Link } from "@mui/material"
import { styled } from "@mui/material/styles"

const FAQContainer = styled(Container)(({ theme }) => ({
  maxWidth: "900px",
  padding: theme.spacing(2.5),
  margin: "0 auto",
}))

const FAQTitle = styled(Typography)(({ theme }) => ({
  fontSize: "42px",
  fontWeight: 700,
  color: "#0057FF",
  textAlign: "center",
  marginBottom: theme.spacing(6),
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down("md")]: {
    fontSize: "32px",
    marginBottom: theme.spacing(4),
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
  },
}))

const FAQItem = styled(Box)(({ theme }) => ({
  display: "flex",
  marginBottom: theme.spacing(4.5),
  alignItems: "flex-start",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
  },
}))

const FAQNumberBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#e9eefb",
  color: "#0057FF",
  fontSize: "30px",
  fontWeight: 700,
  minWidth: "60px",
  height: "60px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  marginRight: theme.spacing(3),
  flexShrink: 0,
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down("sm")]: {
    marginRight: 0,
    marginBottom: theme.spacing(2),
    alignSelf: "flex-start",
  },
}))

const FAQContent = styled(Box)({
  flexGrow: 1,
})

const FAQQuestion = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 700,
  color: "#0057FF",
  marginTop: 0,
  marginBottom: theme.spacing(1.25),
  fontFamily: "'Poppins', sans-serif",
  lineHeight: 1.3,
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
  },
}))

const FAQAnswer = styled(Box)(({ theme }) => ({
  fontSize: "16px",
  lineHeight: 1.7,
  color: "#202020",
  fontFamily: "'Poppins', sans-serif",
  "& p": {
    margin: 0,
    marginBottom: theme.spacing(1.25),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  "& ul": {
    paddingLeft: theme.spacing(2.5),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.25),
    "& ul": {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
  },
  "& li": {
    marginBottom: theme.spacing(1),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  "& a": {
    color: "#0057FF",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  "& strong": {
    fontWeight: 700,
  },
}))

const faqData = [
  {
    id: 1,
    question: "What is the Educator ThoughtCoCreate (TCC) Summit 2025?",
    answer: (
      <Typography component="p">
        It's the Final Summit for the Top 10 Finalists of the Educator ThoughtCoCreate 2025 Challenge, organized by
        Pythaverse. It's an exclusive 4-day, 3-night experience in Sapa & Hanoi, Vietnam, designed for finalists to
        present their solutions, participate in workshops, network, and celebrate collaborative excellence.
      </Typography>
    ),
  },
  {
    id: 2,
    question: "Who is invited to this event?",
    answer: (
      <Typography component="p">
        The Top 10 Finalists of the Educator ThoughtCoCreate 2025 Challenge and other prestigious guests are invited.
      </Typography>
    ),
  },
  {
    id: 3,
    question: "When and where will the TCC Summit take place?",
    answer: (
      <Box>
        <Typography component="p">The overall trip is from July 4 - July 7, 2025.</Typography>
        <Typography component="p">The main Summit Day is July 5, 2025.</Typography>
        <Typography component="p">
          The Summit Venue is the Meeting Room, Sapa Highland Resort and Spa (07 Thac Bac Street, Sapa District, Lao Cai
          Province, Vietnam).
        </Typography>
        <Typography component="p">The event locations are Sapa & Hanoi, Vietnam.</Typography>
      </Box>
    ),
  },
  {
    id: 4,
    question: "What expenses will Pythaverse cover for finalists?",
    answer: (
      <Box>
        <Typography component="p">Pythaverse will cover:</Typography>
        <List dense sx={{ pl: 0 }}>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText
              primary={
                <>
                  <strong>International/Domestic Flights:</strong> Round-trip economy class airfare to Noi Bai
                  International Airport (HAN), Hanoi, Vietnam.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText
              primary={
                <Box>
                  <strong>Accommodation:</strong>
                  <List dense sx={{ mt: 0.5 }}>
                    <ListItem sx={{ display: "list-item", listStyleType: "circle", ml: 2 }}>
                      <ListItemText primary="A half-day stay at Paragon Noi Bai and Pool Hotel (near Noi Bai Airport, Hanoi) for delegates arriving before 11:00 AM on July 4." />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", listStyleType: "circle", ml: 2 }}>
                      <ListItemText primary="2 nights at Sapa Highland Resort and Spa in Sapa (July 4 & 5)." />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", listStyleType: "circle", ml: 2 }}>
                      <ListItemText primary="1 night at the Harmony hotel in Hanoi (July 6)." />
                    </ListItem>
                  </List>
                </Box>
              }
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText
              primary={
                <>
                  <strong>Local Transportation:</strong> Airport transfers in Hanoi, private group transportation
                  between Hanoi and Sapa, and transportation to official group activities.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText
              primary={
                <>
                  <strong>Meals:</strong> Breakfast at hotels (July 5-7), Lunch and Dinner (July 4-6), and refreshments
                  during the TCC Summit.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText
              primary={
                <>
                  <strong>Summit Participation:</strong> Full access to all TCC Summit sessions and materials.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText
              primary={
                <>
                  <strong>Travel Insurance:</strong> For the trip.
                </>
              }
            />
          </ListItem>
        </List>
      </Box>
    ),
  },
  {
    id: 5,
    question: "What expenses are NOT covered by Pythaverse?",
    answer: (
      <Box>
        <Typography component="p">Not included are:</Typography>
        <List dense sx={{ pl: 0 }}>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Visa application fees (if applicable)." />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Personal expenses, souvenirs." />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Optional activities/tours during free & easy time." />
          </ListItem>
        </List>
      </Box>
    ),
  },
  {
    id: 6,
    question: "What if I arrive early in Hanoi on July 4th?",
    answer: (
      <Typography component="p">
        If you arrive at Noi Bai International Airport (HAN) before 11:00 AM on July 4, 2025, Pythaverse will provide a
        half-day stay at the Paragon Noi Bai and Pool Hotel for you to rest before the group departs for Sapa at 14:00
        (2:00 PM).
      </Typography>
    ),
  },
  {
    id: 7,
    question: "How will we travel between Hanoi and Sapa?",
    answer: (
      <Typography component="p">
        You will travel via comfortable, private group transfer (private coach/bus).
      </Typography>
    ),
  },
  {
    id: 8,
    question: "Do I need a visa for Vietnam?",
    answer: (
      <Box>
        <Typography component="p">Visa requirements depend on your nationality.</Typography>
        <List dense sx={{ pl: 0 }}>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText
              primary={
                <>
                  <strong>For citizens of Malaysia:</strong> You are generally exempt from a visa for stays up to 30
                  days in Vietnam.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText
              primary={
                <>
                  <strong>For citizens of Indonesia:</strong> You are generally exempt from a visa for stays up to 30
                  days in Vietnam.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText
              primary={
                <>
                  <strong>For citizens of the Philippines:</strong> You are generally exempt from a visa for stays up to
                  21 days in Vietnam.
                </>
              }
            />
          </ListItem>
        </List>
        <Typography component="p">
          Given the summit duration (July 4-7, 2025, which is 4 days), attendees from Malaysia, Indonesia, and the
          Philippines typically do not require a visa for this trip, provided they meet other standard entry
          requirements (such as having a passport valid for at least 6 months beyond your intended stay and potentially
          proof of onward/return travel).
        </Typography>
        <Typography component="p">
          <strong>Important Note:</strong> While this information is based on current common bilateral agreements, visa
          policies can occasionally change. It is always highly recommended to double-check the latest visa requirements
          with the Vietnamese Embassy or Consulate in your respective country, or consult their official immigration
          website, closer to your travel date to ensure a smooth journey.
        </Typography>
        <Typography component="p">
          Pythaverse does not cover visa application fees (if they were to become applicable); this would be a personal
          expense.
        </Typography>
      </Box>
    ),
  },
  {
    id: 9,
    question: "What can finalists expect to do during the event?",
    answer: (
      <Box>
        <Typography component="p">Finalists will:</Typography>
        <List dense sx={{ pl: 0 }}>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Present their solution to a panel of international judges (on July 5th)." />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Participate in the TCC Summit sessions, workshops, and knowledge-sharing sessions." />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Network with fellow finalists and the Pythaverse team." />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Attend a TCC Summit Celebration Dinner." />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Explore Sapa, including an excursion to minority villages." />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Have some free time for personal exploration in Sapa and Hanoi." />
          </ListItem>
        </List>
      </Box>
    ),
  },
  {
    id: 10,
    question: "Can you give a brief overview of the 4-day itinerary?",
    answer: (
      <List dense sx={{ pl: 0 }}>
        <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
          <ListItemText
            primary={
              <>
                <strong>Day 1 (July 4):</strong> Arrive in Hanoi, transfer to Sapa, check into Sapa Highland Resort.
              </>
            }
          />
        </ListItem>
        <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
          <ListItemText
            primary={
              <>
                <strong>Day 2 (July 5):</strong> Morning free exploration in Sapa, afternoon TCC Summit (presentations,
                workshops), evening TCC Summit Celebration Dinner.
              </>
            }
          />
        </ListItem>
        <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
          <ListItemText
            primary={
              <>
                <strong>Day 3 (July 6):</strong> Morning exploration to minority villages in Sapa, check-out, return to
                Hanoi, check into Harmony hotel.
              </>
            }
          />
        </ListItem>
        <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
          <ListItemText
            primary={
              <>
                <strong>Day 4 (July 7):</strong> Morning free exploration in Hanoi, check-out, transfer to airport for
                departure.
              </>
            }
          />
        </ListItem>
      </List>
    ),
  },
  {
    id: 11,
    question: "Will there be a detailed agenda for the TCC Summit sessions on July 5th?",
    answer: (
      <Typography component="p">
        Yes, the "Detail Summit agenda will be released soon." The invitation mentions it will include finalist
        presentations, workshops, and an award ceremony.
      </Typography>
    ),
  },
  {
    id: 12,
    question: 'What can I do during the "free & easy exploration" time?',
    answer: (
      <Typography component="p">
        During free time, you can explore Sapa (e.g., The Stone Church, Sapa Market, traditional herbal bath,
        spa/swimming at the hotel) and Hanoi (e.g., Hoan Kiem Lake & Ngoc Son Temple, Old Quarter, Temple of Literature,
        Ho Chi Minh Mausoleum complex). These are optional, and any costs associated with them are personal expenses.
      </Typography>
    ),
  },
  {
    id: 13,
    question: "What awards and recognition will finalists receive?",
    answer: (
      <Box>
        <Typography component="p">
          <strong>All Invited Summit Attendees:</strong> Will receive a special welcoming gift from Pythaverse.
        </Typography>
        <Typography component="p">
          <strong>All Finalists:</strong>
        </Typography>
        <List dense sx={{ pl: 0 }}>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="A TCC Summit Award." />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="A Certificate of Participation." />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="A Personalized Whitepaper Set (developed based on your proposal, sent post-summit)." />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Be featured in the ThoughtCoCreate 2025 Whitepaper." />
          </ListItem>
        </List>
        <Typography component="p">
          <strong>Top 3 Winners:</strong> Will be announced and presented with exclusive TCC Summit Trophies (Gold,
          Silver, or Bronze Awards) during a dedicated awards segment.
        </Typography>
      </Box>
    ),
  },
  {
    id: 14,
    question: "How do I confirm my participation?",
    answer: (
      <Typography component="p">
        You need to complete the registration form by June 18, 2025, using the link provided in the invitation:{" "}
        <Link href="https://forms.gle/WC4qsUx7LzcQqQ717" target="_blank" rel="noopener noreferrer">
          https://forms.gle/WC4qsUx7LzcQqQ717
        </Link>
      </Typography>
    ),
  },
  {
    id: 15,
    question: "Who should I contact if I have questions?",
    answer: (
      <Box>
        <Typography component="p">
          For questions or assistance, you can contact Mrs. Trinh (Bach To Trinh), the Event Coordinator:
        </Typography>
        <List dense sx={{ pl: 0 }}>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText
              primary={
                <>
                  Email: <Link href="mailto:trinh@pythaverse.net">trinh@pythaverse.net</Link>
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: "list-item", listStyleType: "disc", ml: 2 }}>
            <ListItemText primary="Phone: +84 912 090 174" />
          </ListItem>
        </List>
      </Box>
    ),
  },
  {
    id: 16,
    question: "Is the itinerary fixed?",
    answer: (
      <Typography component="p">
        The schedule is tentative and subject to change based on actual circumstances as determined by the Organizing
        Committee.
      </Typography>
    ),
  },
]

export default function FAQSummitPage() {
  return (
    <Box sx={{ backgroundColor: "#fff", minHeight: "100vh", py: 4 }}>
      <FAQContainer>
        <FAQTitle variant="h1">
          Frequently Asked Questions
        </FAQTitle>

        {faqData.map((faq) => (
          <FAQItem key={faq.id}>
            <FAQNumberBox>
              <span>{faq.id}</span>
            </FAQNumberBox>
            <FAQContent>
              <FAQQuestion variant="h3">
                {faq.question}
              </FAQQuestion>
              <FAQAnswer>{faq.answer}</FAQAnswer>
            </FAQContent>
          </FAQItem>
        ))}
      </FAQContainer>
    </Box>
  )
}
